import { useState, type FormEvent } from "react";
import { API_KEY_NAME_MAX, API_SCOPES, DEFAULT_API_SCOPES, type ApiKeysView, type ApiScope } from "../../shared/apiV1";
import { PLAN_LABEL, planWith } from "../../shared/market";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "./api";
import { UpgradeLink } from "./Showcase";
import { IntCard, SecretReveal, useWhen } from "./IntegrationsParts";
import { WebhooksSection } from "./WebhooksSection";

/** Ajustes, on plans without the API: one line on what it would do, and the way to the plan that has it. */
export function IntegrationsTeaser({ listings }: { /** The shop's live listings. */ listings: number }) {
  const { t } = useI18n();
  const plan = planWith("api");
  return (
    <section className="plan-card" aria-label={t("api.title")}>
      <p className="small">
        <strong>{t("api.title")}.</strong> {listings > 1 ? t("api.teaser", { count: listings, plan: PLAN_LABEL[plan] }) : t("api.teaserShort", { plan: PLAN_LABEL[plan] })} <UpgradeLink source="vidriera:api" className="btn btn-link btn-sm">{t("api.teaserCta", { plan: PLAN_LABEL[plan] })}</UpgradeLink>
      </p>
    </section>
  );
}

/**
 * Mi tienda › Integraciones (VMAX): the shop's API keys for /api/v1, its webhooks and the log of its
 * requests. A new key is shown once, to copy; the list only has its name, prefix, what it can do and
 * when it was last used. The owner's only: the panel doesn't show it to the admin working as the shop.
 */
export function IntegrationsPage() {
  const { t } = useI18n();
  const keys = useAsync((signal) => marketApi.apiKeys(signal), []);
  const [view, setView] = useState<ApiKeysView | null>(null);
  const current = view ?? keys.data;
  if (!current) return keys.error ? <ErrorMessage onRetry={keys.reload} /> : <Loading />;

  // The plan lapsed while here: the way back to VMAX.
  if (current.max === 0) {
    const plan = planWith("api");
    return (
      <section className="int-card stack-sm" aria-label={t("api.title")}>
        <p>{t("api.lapsed", { plan: PLAN_LABEL[plan] })}</p>
        <div>
          <UpgradeLink source="vidriera:api">{t("api.teaserCta", { plan: PLAN_LABEL[plan] })}</UpgradeLink>
        </div>
      </section>
    );
  }

  return (
    <div className="int stack">
      <div className="int-intro">
        <p>{t("api.hint")}</p>
        <dl className="int-base">
          <dt>{t("api.baseUrl")}</dt>
          <dd>
            <code>{`${window.location.origin}/api/v1`}</code>
          </dd>
        </dl>
        {/* A page of its own (not the app): a full load. */}
        <a className="btn btn-outline btn-sm" href="/desarrolladores">
          {t("api.docs")}
        </a>
      </div>
      <ApiKeys view={current} onView={setView} />
      <WebhooksSection />
      <RequestLog />
    </div>
  );
}

function ApiKeys({ view, onView }: { view: ApiKeysView; onView: (v: ApiKeysView) => void }) {
  const { t } = useI18n();
  const when = useWhen();
  const [adding, setAdding] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const full = view.keys.length >= view.max;

  const create = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const scopes = API_SCOPES.filter((s) => f.get(`scope-${s}`) === "on");
    setBusy(true);
    setError(null);
    try {
      const r = await marketApi.createApiKey(String(f.get("name") ?? "").trim(), scopes);
      onView({ keys: r.keys, max: r.max });
      setCreated(r.key);
      setAdding(false);
    } catch (err) {
      setError(err instanceof ApiError && err.code === "api_key_limit" ? t("api.limit", { max: view.max }) : t("error.generic"));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: number, name: string) => {
    if (!window.confirm(t("api.revokeConfirm", { name }))) return;
    setError(null);
    try {
      onView(await marketApi.revokeApiKey(id));
    } catch {
      setError(t("error.generic"));
    }
  };

  return (
    <IntCard
      id="api-keys"
      title={t("api.keysTitle")}
      meta={t("api.count", { count: view.keys.length, max: view.max })}
      action={
        !adding &&
        !full && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => (setAdding(true), setCreated(null))}>
            {t("api.new")}
          </button>
        )
      }
    >
      <p className="muted small">{t("api.keysHint")}</p>
      {created && <SecretReveal title={t("api.createdTitle")} note={t("api.created")} secret={created} />}
      {adding && (
        <form className="int-form stack-sm" onSubmit={(e) => void create(e)}>
          <div className="field">
            <label htmlFor="api-name">{t("api.name")}</label>
            <input id="api-name" name="name" required maxLength={API_KEY_NAME_MAX} placeholder={t("api.namePlaceholder")} autoFocus />
          </div>
          <fieldset className="check-list">
            <legend>{t("api.scopes")}</legend>
            {API_SCOPES.map((s: ApiScope) => (
              <label key={s}>
                <input type="checkbox" name={`scope-${s}`} defaultChecked={DEFAULT_API_SCOPES.includes(s)} /> {t(`api.scope.${s}` as never)}
              </label>
            ))}
          </fieldset>
          <div className="row-actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              {t("api.create")}
            </button>
            <button type="button" className="btn btn-link btn-sm" onClick={() => setAdding(false)}>
              {t("api.cancel")}
            </button>
          </div>
        </form>
      )}
      {view.keys.length === 0 ? (
        !adding && <p className="int-empty">{t("api.empty")}</p>
      ) : (
        <table className="stats-table int-table">
          <thead>
            <tr>
              <th scope="col">{t("api.colName")}</th>
              <th scope="col">{t("api.colKey")}</th>
              <th scope="col">{t("api.colScopes")}</th>
              <th scope="col">{t("api.colLastUsed")}</th>
              <th scope="col">
                <span className="sr-only">{t("api.colActions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {view.keys.map((k) => (
              <tr key={k.id}>
                <td data-label={t("api.colName")}>
                  <strong>{k.name}</strong>
                </td>
                <td data-label={t("api.colKey")}>
                  <code className="small">{k.prefix}…</code>
                </td>
                <td data-label={t("api.colScopes")}>
                  <span className="int-chips">
                    {k.scopes.map((s) => (
                      <span key={s} className="pill">
                        {t(`api.scopeShort.${s}` as never)}
                      </span>
                    ))}
                  </span>
                </td>
                <td data-label={t("api.colLastUsed")} className="int-nowrap">{k.lastUsedAt ? when(k.lastUsedAt) : <span className="muted">{t("api.neverUsed")}</span>}</td>
                <td className="int-actions">
                  <button type="button" className="btn btn-link btn-sm int-danger" onClick={() => void revoke(k.id, k.name)}>
                    {t("api.revoke")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {full && <p className="muted small">{t("api.limit", { max: view.max })}</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </IntCard>
  );
}

/** The shop's latest requests to the API: what its integration did and what failed. */
function RequestLog() {
  const { t } = useI18n();
  const when = useWhen();
  const [open, setOpen] = useState(false);
  const data = useAsync((signal) => (open ? marketApi.apiRequests(signal) : Promise.resolve(null)), [open]);
  return (
    <IntCard
      id="api-log"
      title={t("api.log")}
      meta={t("api.logHint")}
      action={
        <button type="button" className="btn btn-outline btn-sm" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? t("api.logHide") : t("api.logShow")}
        </button>
      }
    >
      {open &&
        data.data &&
        (data.data.length === 0 ? (
          <p className="int-empty">{t("api.logEmpty")}</p>
        ) : (
          <table className="stats-table int-table">
            <thead>
              <tr>
                <th scope="col">{t("api.colDate")}</th>
                <th scope="col">{t("api.colRequest")}</th>
                <th scope="col">{t("api.colStatus")}</th>
                <th scope="col" className="num">
                  {t("api.colMs")}
                </th>
                <th scope="col">{t("api.colKeyName")}</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((r) => (
                <tr key={r.requestId}>
                  <td data-label={t("api.colDate")} className="int-nowrap">
                    {when(r.at)}
                  </td>
                  <td data-label={t("api.colRequest")}>
                    <code className="int-path">
                      {r.method} {r.path}
                    </code>
                  </td>
                  <td data-label={t("api.colStatus")}>
                    <span className={r.status >= 400 ? "pill pill-warn" : "pill pill-paid"}>{r.status}</span>
                  </td>
                  <td data-label={t("api.colMs")} className="num">
                    {r.ms} ms
                  </td>
                  <td data-label={t("api.colKeyName")}>{r.key ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}
    </IntCard>
  );
}
